import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    addUnitsOfTime,
    autoinject,
    bind,
    DateRangePicker,
    displayFormatTime,
    Duration,
    Enums,
    formatDuration,
    FormStore,
    getDurationToNow,
    HelpTooltip,
    inject,
    NothingHere,
    slugCase,
    SubTopBar, subUnitsOfTime,
    TasksStore,
} from 'Mri.Web.Common';
import { stringify } from 'qs';
import * as React from 'react';
import { BsFillGridFill } from '@react-icons/all-files/bs/BsFillGridFill';
import { BsListUl } from '@react-icons/all-files/bs/BsListUl';
import { BsTv } from '@react-icons/all-files/bs/BsTv';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { DropdownItem, DropdownMenu, DropdownToggle, Form, UncontrolledButtonDropdown } from 'reactstrap';
import {DisplaysStore} from "./DisplaysStore";
import {AppStore} from "../Core/AppStore";
import {start} from "node:repl";

export interface IDisplayEventLogParams {
    serialNumber: string;
}
export interface IDisplayEventLogListProps extends RouteComponentProps<IDisplayEventLogParams> {}

@autoinject
@observer
export class DisplayEventLogList extends React.Component<IDisplayEventLogListProps, any> {
    public constructor(props: IDisplayEventLogListProps) {
        super(props);
        this.state = {};

        const startTime = this.appStore.storage.displayLogTimes?.start
            ? new Date(this.appStore.storage.displayLogTimes?.start)
            : this.formStore.fields.startTime.value;
        if (!this.appStore.storage.displayLogTimes?.start) {
            startTime.setMonth(startTime.getMonth() - 1);
        } else {
            this.formStore.fields.startTime.setValue(startTime);
        }

        const endTime = this.appStore.storage.displayLogTimes?.end
            ? new Date(this.appStore.storage.displayLogTimes?.end)
            : this.formStore.fields.endTime.value;
        if (!this.appStore.storage.displayLogTimes?.end) {
            endTime.setDate(endTime.getDate() + 1);
            endTime.setMinutes(59);
            endTime.setSeconds(59);
        } else {
            this.formStore.fields.endTime.setValue(endTime);
        }
    }

    @action
    public async componentDidMount() {
        this.loading = true;

        try {
            await this.displaysStore.loadAsync(this.props.match.params.serialNumber);
            await this.getEventLogAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div>
                <SubTopBar>
                    <Form onSubmit={this.onSubmit}>
                        <div className="d-block w-100">
                            <DateRangePicker
                                startDate={this.formStore.fields.startTime}
                                endDate={this.formStore.fields.endTime}
                                className="d-inline-block me-2"
                                timezone={this.displaysStore.timezones.get(this.props.match.params.serialNumber)}
                            />

                            <button className="mx-2 btn btn-primary">{this.loading ? 'Loading...' : 'Apply'}</button>
                        </div>
                    </Form>
                </SubTopBar>

                <div>
                    <table className="table table-striped fixed-header">
                        <thead>
                        <tr>
                            <th>Details</th>
                            <th>Duration</th>
                            <th>Start Time</th>
                            <th>Type</th>
                            <th>Active</th>
                            <th>Status</th>
                        </tr>
                        </thead>
                        <tbody>{this.displayEvents && this.displayEvents.map(this.renderRow)}</tbody>
                    </table>
                    <NothingHere
                        empty={!this.displayEvents?.length}
                        icon={<BsTv />}
                        loading={this.loading}
                        label="No Display Events"
                        loadingMessage="Loading Events..."
                    />
                </div>
            </div>
        );
    }

    @bind
    private renderRow(displayEvent: Mri.DisplayEvents.DisplayEvent) {
        const startTime = displayEvent.downtimeIncidentStartTime ?? displayEvent.creationTime;

        const duration =
            displayEvent.downtimeIncidentDuration != null
                ? new Duration(displayEvent.downtimeIncidentDuration * 1000)
                : displayEvent.downtimeActiveIncident
                    ? getDurationToNow(startTime)
                    : undefined;
        return (
            <tr>
                <td>
                    <HelpTooltip
                        subjects={['Downtime Incidents']}
                        name={slugCase(Enums.toString(Enums.DowntimeIncidentType, displayEvent.downtimeIncidentType))}
                        underline
                        placement="top"
                        className={this.eventSeverityStyle(displayEvent)}
                    >
                        <b className={this.eventSeverityStyle(displayEvent)}>{displayEvent.details}</b>
                    </HelpTooltip>
                </td>
                <td>{duration != null ? formatDuration(duration) : '-'}</td>
                <td>
                    {displayFormatTime(
                        startTime,
                        'EEEE, MMMM do yyyy, h:mm:ss a zzz',
                        this.props.match.params.serialNumber
                    )}
                </td>
                <td>
                    <div className={this.incidentClass(displayEvent) + ' me-1'}>
                        {Enums.toString(Enums.DisplayIncidentLevel, displayEvent.incidentLevel)}
                    </div>
                </td>
                <td>
                    <div>
                        {displayEvent.downtimeActiveIncident ? "Active" : "Inactive"}
                    </div>
                </td>
                <td>
                    <div className={this.badgeClass(displayEvent)}>
                        {Enums.toString(Enums.DisplayEventStatus, displayEvent.displayEventStatus)}
                    </div>
                </td>
            </tr>
        );
    }

    @bind
    private onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        this.getEventLogAsync();
    }

    @bind
    @action
    private async getEventLogAsync() {
        this.loading = true;
        let startTime = new Date(this.formStore.fields.startTime.value);
        let endTime = new Date(this.formStore.fields.endTime.value);
        let twoYearsAgo =  subUnitsOfTime(new Date(), 2, 'years');
        let monthInFuture = addUnitsOfTime(new Date(), 1, 'month');

        if(startTime < twoYearsAgo) startTime = subUnitsOfTime(new Date(), 1, 'month');
        if(endTime > monthInFuture) endTime = new Date();

        this.appStore.storage.displayLogTimes = {
            start: startTime,
            end: endTime,
        };
        try {
            const events = await this.displaysStore.getEventHistoryAsync(
                this.props.match.params.serialNumber,
                startTime,
                endTime
            );
            if (events) this.displayEvents = events;
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private badgeClass(displayEvent: Mri.DisplayEvents.DisplayEvent) {
        const badge = 'badge ';
        if (displayEvent.displayEventStatus == Mri.DisplayEvents.DisplayEventStatus.Alert) {
            return badge + 'bg-warning';
        }
        return badge + 'bg-secondary';
    }

    @bind
    @action
    private incidentClass(displayEvent: Mri.DisplayEvents.DisplayEvent) {
        const badge = 'badge ';

        let color = '';

        switch (displayEvent.incidentLevel) {
            default:
                color = 'bg-danger';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentCritical:
                color = 'bg-danger';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentMajor:
                color = 'bg-danger';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentMinor:
                color = 'bg-warning';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentNotification:
                color = 'bg-teal';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentIgnored:
                color = 'bg-secondary';
                break;
        }

        return badge + color;
    }

    @bind
    @action
    private eventSeverityStyle(displayEvent: Mri.DisplayEvents.DisplayEvent) {
        let color = '';
        if (displayEvent.downtimeActiveIncident) {
            switch (displayEvent.incidentLevel) {
                default:
                    color = 'text-danger';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentCritical:
                    color = 'text-danger';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentMajor:
                    color = 'text-danger';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentMinor:
                    color = 'text-warning';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentNotification:
                    color = 'text-teal';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentIgnored:
                    color = 'text-muted';
                    break;
            }
        } else color = 'text-muted';

        return color;
    }

    private formStore = new FormStore({
        startTime: new Date(),
        endTime: new Date(),
    });

    @observable
    private loading = false;
    @observable
    private displayEvents?: Mri.DisplayEvents.DisplayEvent[];
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private tasksStore!: TasksStore;
}
